import { config } from './config'
import { getAccessToken } from './authToken'
import { refreshSession } from './http'

/**
 * Server-sent events, for every surface in this app that has one.
 *
 * ── Lifted out of `features/copilot/stream.js`, not rewritten ────────────
 * The copilot's turn stream was the first SSE reader here and it is the one
 * piece of this codebase that has already survived a real bug in the wild: the
 * server writes CRLF, and a parser looking only for `\n\n` finds no boundary at
 * all and silently yields nothing — not an error, not a partial answer, just an
 * empty stream. It also handles a frame split across two network chunks, which
 * is the other way a naive reader loses the tail of an event.
 *
 * Both of those are solved below, moved verbatim. Anomalies needed a second
 * reader; a second reader would have meant learning both lessons twice.
 *
 * ── `fetch` and not `EventSource`, and it is not a preference ────────────
 * Four separate reasons, any one of which is on its own sufficient:
 *
 *   1. `EventSource` reconnects forever to a FIXED URL. These routes take the
 *      credential as `?token=` (see `auth` below), so its own reconnect would
 *      retry with a baked-in expired token until the tab is closed.
 *   2. It exposes no status code. A 403 (missing permission), a 422 (a
 *      server-side stream cap) and dead Wi-Fi are indistinguishable — and a
 *      client that cannot see a 422 will hammer a capped endpoint forever.
 *   3. It is GET-only, so the copilot — which POSTs a question in a body —
 *      could never share the code path.
 *   4. `fetch` + `AbortController` is what this app already does against this
 *      same server.
 */

/**
 * Whether a stream may be opened at all.
 *
 * `authMode`, not `mode`: streaming resources sit in `LIVE_PATHS` regardless of
 * `VITE_API_MODE`, and "is the backend running" is the only question that
 * matters here. `AppShell` already draws the line in exactly this place.
 *
 * The `window` guard is for the verify scripts, which import this graph under
 * Node — there is no `fetch` body reader to drive there and nothing to stream.
 */
export function sseEnabled() {
  return typeof window !== 'undefined' && config.authMode === 'live'
}

/** A non-2xx on the stream request itself. Carries the status, which is the point. */
export class SseHttpError extends Error {
  constructor(status) {
    super(`stream refused with ${status}`)
    this.name = 'SseHttpError'
    this.status = status
  }
}

/**
 * One SSE frame into `{event, ...data}`.
 *
 * A frame whose data will not parse is dropped rather than thrown: one
 * malformed event should cost that event, not the rest of the answer. A comment
 * frame (`: keepalive`) has no `data:` line and falls out the same way, which is
 * what makes a heartbeat safe to add server-side later without touching this.
 */
export function parseSseFrame(frame) {
  let name = 'message'
  const dataLines = []

  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) name = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
  }
  if (!dataLines.length) return null

  try {
    return { event: name, ...JSON.parse(dataLines.join('\n')) }
  } catch {
    return null
  }
}

/**
 * Drain a response body of SSE frames, handing each to `onEvent` as it lands.
 *
 * Resolves when the body ends. Two things here are load-bearing and neither is
 * obvious from reading the spec:
 *
 * Line endings are normalised FIRST. The spec allows CRLF, LF and CR, and the
 * server uses the one a `\n\n` search does not find.
 *
 * The buffer is consumed only up to the LAST COMPLETE frame boundary. Parsing
 * per chunk would drop the tail of any event unlucky enough to straddle one,
 * which on a slow connection is most of them.
 */
export async function pumpSseBody(body, onEvent) {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n')

    const lastBreak = buffer.lastIndexOf('\n\n')
    if (lastBreak === -1) continue

    for (const frame of buffer.slice(0, lastBreak).split('\n\n')) {
      const parsed = parseSseFrame(frame)
      if (parsed) onEvent(parsed)
    }
    buffer = buffer.slice(lastBreak + 2)
  }
}

/**
 * One stream request, start to finish.
 *
 * ── `auth` ───────────────────────────────────────────────────────────────
 * `'query'` puts the access token in `?token=`, which the anomaly streams
 * REQUIRE — `get_current_user_from_query_token` declares `token: str` with no
 * default, so it is a mandatory query parameter and there is no header path.
 * That is the server's own accommodation for `EventSource`, and we inherit it
 * even though we do not use `EventSource`.
 *
 * `'header'` sends a normal `Authorization` header, which is what a POST-bodied
 * stream should do.
 *
 * ── A 401 is retried exactly once ────────────────────────────────────────
 * The token is a CONNECT-TIME credential: the dependency authenticates once and
 * the stream then runs for as long as it runs. So an expired token shows up as
 * a 401 on connect, never mid-stream. `refreshSession` already de-dupes
 * concurrent callers through its own in-flight promise, so a stream reconnect
 * racing an ordinary XHR's 401 share one refresh rather than causing two.
 *
 * Throws `SseHttpError` on any other non-2xx so the caller can tell a refusal
 * from a blip and stop retrying.
 */
export async function openSseStream({
  path,
  method = 'GET',
  body,
  auth = 'query',
  signal,
  onEvent,
  onOpen,
}) {
  const response = await connect({ path, method, body, auth, signal, retryOn401: true })

  if (!response.ok || !response.body) {
    throw new SseHttpError(response.status)
  }
  onOpen?.()
  await pumpSseBody(response.body, onEvent)
}

async function connect({ path, method, body, auth, signal, retryOn401 }) {
  // Read at every attempt and never cached on the connection: a reconnect then
  // picks up a rotated token by itself, which is the whole rotation story.
  const token = getAccessToken()

  const url =
    auth === 'query'
      ? `${config.apiBaseUrl}${path}${path.includes('?') ? '&' : '?'}token=${encodeURIComponent(token ?? '')}`
      : `${config.apiBaseUrl}${path}`

  const response = await fetch(url, {
    method,
    signal,
    headers: {
      Accept: 'text/event-stream',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(auth === 'header' && token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })

  if (response.status === 401 && retryOn401) {
    await refreshSession()
    return connect({ path, method, body, auth, signal, retryOn401: false })
  }
  return response
}

/**
 * Strip the credential before a URL is logged.
 *
 * `?token=` is the server's contract and nothing here can change it, so the
 * token will appear in access logs and in the network panel regardless. What
 * this stops is the avoidable half: a console line or an error report carrying
 * a live token out of the browser.
 */
export const redactToken = (url) => String(url).replace(/([?&]token=)[^&]*/g, '$1REDACTED')

/** Backoff: 1s, 2, 4, 8, capped at 15 — with full jitter, so N tabs do not sync up. */
const BACKOFF_CAP_MS = 15_000
const backoffFor = (failures) =>
  Math.random() * Math.min(BACKOFF_CAP_MS, 1000 * 2 ** Math.max(0, failures - 1))

/** A connection that lasted this long is treated as healthy; the backoff resets. */
const HEALTHY_MS = 10_000

/** Consecutive failures before giving up and letting the caller fall back. */
const MAX_FAILURES = 5

/** How long a tab may sit hidden before the socket is closed rather than idled. */
const HIDDEN_CLOSE_MS = 5 * 60_000

/**
 * A self-reconnecting stream that several callers can share.
 *
 * ── Ref-counted, because two hooks must not mean two sockets ─────────────
 * On the anomalies screen the list page and the app shell's severity band are
 * mounted at the same time and both want the same stream. A per-hook connection
 * would open two, and on HTTP/1.1 the six-per-origin cap is close enough that
 * spare connections are not free — once it is reached every ordinary request
 * blocks indefinitely, which presents as the app freezing rather than as an
 * error.
 *
 * So the socket opens on the first subscriber and closes `lingerMs` after the
 * last. The linger is what stops list → detail → back from churning a Postgres
 * LISTEN three times in two seconds.
 *
 * ── A hidden tab keeps its socket ────────────────────────────────────────
 * An idle SSE connection costs nothing — the server pushes only when something
 * changes — while closing on every alt-tab costs a reconnect, a fresh LISTEN
 * and a mandatory catch-up read each time. That is strictly worse than the poll
 * this replaces. The caller is told to suppress its own work instead, via
 * `onStatus`/`onEvent` ordering, and after five minutes hidden the socket does
 * close, which covers the tab left open for a week.
 */
export function createSharedSseConnection({ path, lingerMs = 0, onEvent, onOpen, onStatus }) {
  let subscribers = 0
  let controller = null
  let lingerTimer = null
  let hiddenTimer = null
  let running = false
  let failures = 0
  let status = 'idle'
  /* Whether a socket has ever been live on this connection. What it buys the
     caller: there is no `Last-Event-ID` replay here, so a gap is itself news —
     anything could have changed while the stream was down, and a reconnect has
     to be treated as "refetch", not as "carry on". */
  let everConnected = false

  const setStatus = (next) => {
    if (status === next) return
    status = next
    onStatus?.(next)
  }

  async function loop() {
    if (running) return
    running = true

    while (subscribers > 0 && failures < MAX_FAILURES) {
      controller = new AbortController()
      const startedAt = Date.now()
      setStatus('connecting')

      try {
        await openSseStream({
          path,
          signal: controller.signal,
          onOpen: () => {
            failures = 0
            setStatus('live')
            onOpen?.({ reconnected: everConnected })
            everConnected = true
          },
          onEvent,
        })
        // A clean end is still an end. Reconnect unless we are shutting down.
        if (subscribers === 0) break
      } catch (error) {
        if (controller.signal.aborted) break
        /**
         * A refusal, not a blip. Retrying any of these is a loop that can never
         * succeed and that the server has to keep answering.
         *
         *   403  the permission is missing
         *   422  a FastAPI-level validation failure
         *   400  an application refusal — `ValidationAppError`, which is what
         *        every server-side cap on this API actually raises
         *
         * 400 was missing, and that is the one that bit. The anomaly sensor-tag
         * stream answers `ValidationAppError("Too many anomaly sensor-tag
         * streams already open.")` when `try_acquire` is at its ceiling, and
         * that is a 400 — so a capped stream fell through to the backoff and
         * retried forever, cycling connecting → retrying → connecting. On screen
         * that is a card stuck on "Connecting…" that will never resolve, against
         * a server that is never going to say yes.
         *
         * Nothing on these routes returns a 400 that a retry would fix: the
         * parameters are in the path and the token is in the query, so a
         * malformed request is malformed every time.
         */
        if (
          error instanceof SseHttpError &&
          (error.status === 400 || error.status === 403 || error.status === 422)
        ) {
          setStatus('failed')
          running = false
          return
        }
        failures += 1
      }

      if (Date.now() - startedAt >= HEALTHY_MS) failures = 0
      if (subscribers === 0 || failures >= MAX_FAILURES) break

      setStatus('retrying')
      await sleep(backoffFor(failures))
    }

    running = false
    setStatus(failures >= MAX_FAILURES ? 'failed' : 'idle')
  }

  const stop = () => {
    controller?.abort()
    controller = null
  }

  const onVisibility = () => {
    if (document.hidden) {
      // Idle rather than close — see the header. The long timer is the escape
      // hatch for a tab nobody comes back to.
      hiddenTimer = setTimeout(stop, HIDDEN_CLOSE_MS)
      return
    }
    clearTimeout(hiddenTimer)
    hiddenTimer = null
    // Coming back is the one moment worth an immediate attempt rather than a
    // backoff wait: the connection may have died while nobody was looking.
    if (subscribers > 0 && !running) {
      failures = 0
      loop()
    }
  }

  const onOnline = () => {
    if (subscribers > 0 && !running) {
      failures = 0
      loop()
    }
  }

  return {
    subscribe() {
      if (!sseEnabled()) return () => {}

      subscribers += 1
      clearTimeout(lingerTimer)
      lingerTimer = null

      if (subscribers === 1) {
        document.addEventListener('visibilitychange', onVisibility)
        window.addEventListener('online', onOnline)
        failures = 0
        loop()
      }

      return () => {
        subscribers -= 1
        if (subscribers > 0) return

        lingerTimer = setTimeout(() => {
          if (subscribers > 0) return
          document.removeEventListener('visibilitychange', onVisibility)
          window.removeEventListener('online', onOnline)
          clearTimeout(hiddenTimer)
          hiddenTimer = null
          stop()
        }, lingerMs)
      }
    },
    get status() {
      return status
    },
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
